import { deleteRequest, getRequest, patchRequest, postRequest, putRequest } from 'features/common'
import {
  AtInstallation,
  AtInstallationSnowflakeConfig,
  AtInstallationState,
  AtInstallationVersions,
  Bucket,
  DatabricksOAuthU2M,
  DatabricksOAuthU2MPayload,
  ExternalIdp
} from '../types'
import settings from 'settings'
import { DEFAULT_ANALYTICS_TOOLBOX_PROJECT } from 'settings'
import { ProviderType } from 'features/connections'
import { queryClient } from 'queryClient'
import { connectionsQuery } from 'features/connections/api/connectionsQueries'
import { versionFromSqlQuery } from 'features/connections/api/connections'

const BASE_BUCKET_URL = `${settings.workspaceApiUrl}/storage/buckets`
const BASE_EXTERNAL_IDP_URL = `${settings.workspaceApiUrl}/integrations/external-idps`

const DEFAULT_AT_LOCATION_BQ = '`carto-un.carto`'

export const getBuckets = async (): Promise<Bucket[] | undefined> => {
  return getRequest(BASE_BUCKET_URL) as Promise<Bucket[]>
}

export const getBucket = async (id: string): Promise<Bucket | undefined> => {
  return getRequest(`${BASE_BUCKET_URL}/${id}`) as Promise<Bucket>
}

export const createBucket = async (bucket: Bucket): Promise<Bucket> => {
  return await postRequest(`${BASE_BUCKET_URL}`, bucket)
}

export const updateBucket = async (bucket: Bucket): Promise<Bucket> => {
  const { integrationType, name, config } = bucket
  return await patchRequest(`${BASE_BUCKET_URL}/${bucket.id}`, { integrationType, name, config })
}

export const deleteBucket = async (bucketId: string): Promise<{ status }> => {
  return await deleteRequest(`${BASE_BUCKET_URL}/${bucketId}`)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const generateS3Policy = async (name: string): Promise<any> => {
  return getRequest(`${BASE_BUCKET_URL}/s3/policy?bucket=${name}`)
}

export const getBuiltinAtInstallations = async (): Promise<AtInstallation[]> => {
  const connections = await queryClient.fetchQuery(connectionsQuery)

  const result: AtInstallation[] = []
  const cartoDwConnection = connections.find(({ name }) => name === 'carto_dw')
  const bigQueryConnection = connections.find(
    ({ provider_id, carto_dw }) => provider_id === ProviderType.BIGQUERY && !carto_dw
  )

  let version = ''
  if (cartoDwConnection && cartoDwConnection.spatial_extension) {
    version = await versionFromSqlQuery(cartoDwConnection, DEFAULT_AT_LOCATION_BQ)
    result.push({
      providerId: ProviderType.CARTO_DW,
      accountId: cartoDwConnection.account_id || '',
      id: `${cartoDwConnection.id}-fake-at-installation`,
      version: version,
      state: AtInstallationState.Available,
      installedAt: cartoDwConnection.created_at,
      location: 'US multiregion'
    })
  }
  if (bigQueryConnection && bigQueryConnection.spatial_extension) {
    version = version || (await versionFromSqlQuery(bigQueryConnection, DEFAULT_AT_LOCATION_BQ))
    result.push({
      providerId: ProviderType.BIGQUERY,
      accountId: bigQueryConnection.account_id || '',
      id: `${bigQueryConnection.account_id}-fake-at-installation`,
      version: version,
      config: {
        account: DEFAULT_ANALYTICS_TOOLBOX_PROJECT
      },
      state: AtInstallationState.Available,
      location: 'All regions'
    })
  }

  return result
}

export const getAtInstallation = async (id: string): Promise<AtInstallation> => {
  return await getRequest(`${settings.workspaceApiUrl}/at-installations/${id}`)
}

export const getAtInstallations = async (): Promise<AtInstallation[]> => {
  return await getRequest(`${settings.workspaceApiUrl}/at-installations`)
}

export const getAtLatestVersions = async (): Promise<AtInstallationVersions> => {
  return await getRequest(`${settings.workspaceApiUrl}/at-latest-version`)
}

export type ValidateAtInstallationCredentialsParams = {
  providerId: ProviderType
  config: AtInstallationSnowflakeConfig
}

export const validateAtInstallationCredentials = async (
  params: ValidateAtInstallationCredentialsParams
): Promise<AtInstallation> => {
  return postRequest(`${settings.workspaceApiUrl}/at-installations`, params)
}

export type InstallAnalyticsToolboxParams = {
  id: string
  apiBaseUrl: string
}

export const installAnalyticsToolbox = async (params: InstallAnalyticsToolboxParams): Promise<AtInstallation> => {
  const { id, ...payload } = params
  return putRequest(`${settings.workspaceApiUrl}/at-installations/${id}/install`, payload)
}

export type UpdateAtInstallationConfigParams = {
  id: string
  config: AtInstallationSnowflakeConfig
}

export const updateAtInstallationConfig = async (params: UpdateAtInstallationConfigParams): Promise<AtInstallation> => {
  const { id, config } = params
  return patchRequest(`${settings.workspaceApiUrl}/at-installations/${id}`, { config })
}

export type DeleteAnalyticsToolboxParams = Pick<AtInstallation, 'id'>

export const deleteAnalyticsToolbox = async ({ id }: DeleteAnalyticsToolboxParams): Promise<void> => {
  await deleteRequest(`${settings.workspaceApiUrl}/at-installations/${id}`)
}

export const getExternalIdps = async (): Promise<ExternalIdp[] | undefined> => {
  return getRequest(`${BASE_EXTERNAL_IDP_URL}`) as Promise<ExternalIdp[]>
}

export const getExternalIdp = async (id: string): Promise<ExternalIdp | undefined> => {
  return getRequest(`${BASE_EXTERNAL_IDP_URL}/${id}`) as Promise<ExternalIdp>
}

export const createExternalIdp = async (externalIdp: ExternalIdp): Promise<ExternalIdp> => {
  return await postRequest(`${BASE_EXTERNAL_IDP_URL}`, externalIdp)
}

export const updateExternalIdp = async (externalIdp: ExternalIdp): Promise<ExternalIdp> => {
  return await patchRequest(`${BASE_EXTERNAL_IDP_URL}/${externalIdp.id}`, externalIdp)
}

export const getJWK = async (): Promise<unknown> => {
  return getRequest(`${settings.workspaceApiUrl}/settings/wif/jwk`)
}

export const getWorkloadIdentityFederation = async (): Promise<{ audience: string }> => {
  return getRequest(`${settings.workspaceApiUrl}/settings/wif`)
}
export const postWorkloadIdentityFederation = async (audience: string): Promise<unknown> => {
  return postRequest(`${settings.workspaceApiUrl}/settings/wif`, { audience })
}

export const deleteWorkloadIdentityFederation = async (): Promise<unknown> => {
  return deleteRequest(`${settings.workspaceApiUrl}/settings/wif/audience`)
}

export const getDatabricksOAuthU2M = async (): Promise<DatabricksOAuthU2M> => {
  return getRequest(`${settings.workspaceApiUrl}/integrations/databricks-oauth`)
}
export const postDatabricksOAuthU2M = async (payload: DatabricksOAuthU2MPayload): Promise<DatabricksOAuthU2M> => {
  return postRequest(`${settings.workspaceApiUrl}/integrations/databricks-oauth`, payload)
}

export const patchDatabricksOAuthU2M = async (payload: DatabricksOAuthU2MPayload): Promise<DatabricksOAuthU2M> => {
  return patchRequest(`${settings.workspaceApiUrl}/integrations/databricks-oauth`, payload)
}

export const deleteDatabricksOAuthU2M = async (): Promise<unknown> => {
  return deleteRequest(`${settings.workspaceApiUrl}/integrations/databricks-oauth`)
}
